import { useState, useEffect } from 'react';
import {
  Button,
  Group,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconCalendarEvent,
  IconCheck,
  IconInfoCircle,
  IconSend,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import { useCustomerProfile, useSubmitStatementCycleRequest } from '@/hooks/usePortalApi';
import type { StatementCycleChangeRequest } from '@/types';

// ─── Constants ───────────────────────────────────────────────────────────────

const CYCLE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
];

const DAY_OF_MONTH_OPTIONS = [
  { value: '1st', label: '1st of month' },
  { value: '15th', label: '15th of month' },
  { value: 'last', label: 'Last day of month' },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function StatementCycleRequest() {
  const { data: profile, isLoading } = useCustomerProfile();
  const submitRequest = useSubmitStatementCycleRequest();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<StatementCycleChangeRequest>({
    initialValues: {
      current_cycle: '',
      requested_cycle: '',
      reason: '',
      preferred_day: '',
    },
    validate: {
      requested_cycle: (v) =>
        !v ? 'Please select a statement cycle' : null,
    },
  });

  const requestedCycle = form.values.requested_cycle;

  // Reset preferred_day when the requested cycle changes to a value that
  // does not support day selection (e.g. daily) or when switching between
  // weekly/biweekly and monthly (different option sets).
  useEffect(() => {
    form.setFieldValue('preferred_day', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedCycle]);

  const showPreferredDay =
    requestedCycle === 'weekly' ||
    requestedCycle === 'biweekly' ||
    requestedCycle === 'monthly';

  const preferredDayOptions =
    requestedCycle === 'monthly' ? DAY_OF_MONTH_OPTIONS : DAY_OF_WEEK_OPTIONS;

  const handleSubmit = (values: typeof form.values) => {
    const payload: StatementCycleChangeRequest = {
      requested_cycle: values.requested_cycle,
      current_cycle: values.current_cycle || undefined,
      reason: values.reason || undefined,
      preferred_day: values.preferred_day || undefined,
    };

    submitRequest.mutate(payload, {
      onSuccess: () => {
        notifications.show({
          title: 'Request Submitted',
          message:
            'Your statement cycle change request has been submitted successfully.',
          color: 'green',
        });
        setSubmitted(true);
      },
      onError: () => {
        notifications.show({
          title: 'Error',
          message: 'Failed to submit your request. Please try again.',
          color: 'red',
        });
      },
    });
  };

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={30} width={280} />
        <Skeleton height={120} />
        <Skeleton height={300} />
      </Stack>
    );
  }

  if (submitted) {
    return (
      <Stack gap="lg">
        <PageHeader
          title="Statement Cycle Change"
          description="Request a change to when you receive your account statements"
        />
        <Paper p="xl" shadow="sm">
          <Stack align="center" gap="md" py="xl">
            <IconCheck size={48} color="var(--mantine-color-green-6)" />
            <Title order={3}>Request Submitted</Title>
            <Text c="dimmed" ta="center" maw={500}>
              Your statement cycle change request has been submitted. Changes
              typically take effect at the start of the next billing period.
            </Text>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Statement Cycle Change"
        description="Request a change to when you receive your account statements"
      />

      {/* Section 1: Current Settings */}
      <Paper p="lg" radius="md" bg="gray.0">
        <Group gap="sm" mb="md">
          <IconInfoCircle size={20} color="var(--mantine-color-gray-6)" />
          <Text fw={700} size="lg">
            Current Settings
          </Text>
        </Group>

        <Stack gap="xs">
          <Group gap="xs">
            <Text size="sm" fw={500}>
              Payment Terms:
            </Text>
            <Text size="sm">{profile?.payment_terms || 'N/A'}</Text>
          </Group>
          <Text size="sm" c="dimmed">
            Your statement cycle is currently derived from your payment terms.
            You can request a specific cycle below.
          </Text>
        </Stack>
      </Paper>

      {/* Section 2: Request Form */}
      <Paper p="lg" radius="md" shadow="sm">
        <Group gap="sm" mb="md">
          <IconCalendarEvent size={20} />
          <Text fw={700} size="lg">
            Request New Cycle
          </Text>
        </Group>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md" maw={500}>
            <Select
              label="Requested Statement Cycle"
              placeholder="Select a cycle"
              data={CYCLE_OPTIONS}
              required
              {...form.getInputProps('requested_cycle')}
            />

            {showPreferredDay && (
              <Select
                label="Preferred Day"
                placeholder="Select preferred day"
                data={preferredDayOptions}
                clearable
                {...form.getInputProps('preferred_day')}
              />
            )}

            <Textarea
              label="Reason / Notes"
              placeholder="Any additional notes about your preference..."
              minRows={3}
              {...form.getInputProps('reason')}
            />

            <Group justify="flex-end" mt="sm">
              <Button
                type="submit"
                leftSection={<IconSend size={16} />}
                loading={submitRequest.isPending}
              >
                Submit Request
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}
